import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import AverageIcon from '../../../../components/discover/aivy-pdf-reports/components/graphs/AverageIcon'
import MandatoryBadge from '../evaluation/MandatoryBadge'

const PREFIX = 'discover.team_profile_section.requirement_overlay'

// Dieselben Farben wie die Bereiche in BeamGraph, rgb(167,247,231) und rgb(253,218,148)
const SWATCHES = [
  { key: 'ideal', color: 'bg-[#A7F7E7]', label: 'global.ideal_area' },
  {
    key: 'transition',
    color: 'bg-[#FDDA94]',
    label: 'global.transition_area'
  }
]

/**
 * Gemeinsame Legende für alle Skalen des Teamprofils, direkt über der ersten
 * Skala. Bevölkerungs- und Teamdurchschnitt stehen immer da, damit die Zeile
 * beim Umschalten nicht springt. Die Einträge der Stelle erscheinen nur, wenn
 * ihre Bereiche sichtbar sind. Die Reihenfolge folgt dem, was man auf der
 * Skala zuerst sieht, Stelle und Bereiche vor den Markern.
 */
export default function TeamProfileLegend({
  careerTitle,
  careerCount = 1,
  showRequirement = false,
  hasMandatory = false,
  hasPartialBands = false,
  isLoading = false,
  className
}) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className={className} aria-busy='true'>
        <Skeleton width={420} height={16} />
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Zwei Gruppen, damit die Zeile zwischen Stelle und Markern umbricht
          und nie ein einzelner Eintrag allein in der zweiten Zeile steht */}
      <div
        role='group'
        aria-label={t(`${PREFIX}.legend_label`)}
        className='flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-rockGrey'
      >
        {showRequirement && (
          <ul className='flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2'>
            {/* Nur der Stellentitel wird gekürzt, der Hinweis auf mehrere
                Stellen bleibt immer sichtbar */}
            <li className='flex min-w-0 items-baseline gap-x-1.5'>
              <span className='flex-shrink-0 font-medium text-darkBlack'>
                {t(`${PREFIX}.legend_requirement`)}
              </span>
              <span
                title={careerTitle}
                className='min-w-0 max-w-[240px] truncate font-medium text-darkBlack'
              >
                {careerTitle}
              </span>
              {careerCount > 1 && (
                <>
                  <span aria-hidden='true'>·</span>
                  <span className='flex-shrink-0 whitespace-nowrap'>
                    {t(`${PREFIX}.legend_first_of`, { total: careerCount })}
                  </span>
                </>
              )}
            </li>
            {SWATCHES.map(({ key, color, label }) => (
              <li key={key} className='flex items-center gap-x-1.5'>
                <span
                  aria-hidden='true'
                  className={clsx('h-2 w-5 rounded-sm', color)}
                />
                {t(label)}
              </li>
            ))}
            {hasMandatory && (
              <li className='flex items-center gap-x-1.5'>
                {/* Das Badge ist hier nur Symbol, vorgelesen wird der Text daneben */}
                <span aria-hidden='true'>
                  <MandatoryBadge />
                </span>
                {t(`${PREFIX}.legend_mandatory`)}
              </li>
            )}
          </ul>
        )}
        <ul className='flex flex-wrap items-center gap-x-4 gap-y-2'>
          <li className='flex items-center gap-x-1.5'>
            <span aria-hidden='true' className='h-4 w-0.5 bg-mediumGrey' />
            {t('global.population_average')}
          </li>
          <li className='flex items-center gap-x-1.5'>
            <span aria-hidden='true' className='relative inline-block h-4 w-4'>
              <AverageIcon left='50%' bottom='50%' />
            </span>
            {t('common.team_average')}
          </li>
        </ul>
      </div>
      {showRequirement && hasPartialBands && (
        <p className='mt-2 text-xs text-rockGrey'>
          {t(`${PREFIX}.partial_hint`)}
        </p>
      )}
    </div>
  )
}

TeamProfileLegend.propTypes = {
  careerTitle: PropTypes.string,
  careerCount: PropTypes.number,
  showRequirement: PropTypes.bool,
  hasMandatory: PropTypes.bool,
  hasPartialBands: PropTypes.bool,
  isLoading: PropTypes.bool,
  className: PropTypes.string
}
